import { useState, useCallback, useRef } from 'react';

/**
 * useTranslation - Auto-translate messages using Cloudflare Workers AI
 *
 * Uses the gjyl-voice-ai worker's /translate endpoint (M2M100 model)
 * Supports 100+ languages including Ukrainian, Russian, etc.
 */

const VOICE_WORKER_URL = 'https://gjyl-voice-ai.gjyl.workers.dev';

// Language codes supported by M2M100
const SUPPORTED_LANGUAGES = {
  'en': 'English',
  'uk': 'Ukrainian',
  'ru': 'Russian',
  'es': 'Spanish',
  'fr': 'French',
  'de': 'German',
  'it': 'Italian',
  'pt': 'Portuguese',
  'zh': 'Chinese',
  'ja': 'Japanese',
  'ko': 'Korean',
  'ar': 'Arabic',
  'hi': 'Hindi',
  'pl': 'Polish',
  'nl': 'Dutch',
  'tr': 'Turkish',
  'vi': 'Vietnamese',
  'th': 'Thai',
  'sv': 'Swedish',
  'da': 'Danish',
  'fi': 'Finnish',
  'no': 'Norwegian',
  'cs': 'Czech',
  'ro': 'Romanian',
  'hu': 'Hungarian',
  'el': 'Greek',
  'he': 'Hebrew',
  'id': 'Indonesian',
  'ms': 'Malay',
};

/**
 * Get browser language code (2-letter)
 */
export function getBrowserLanguage() {
  const lang = navigator.language || navigator.userLanguage || 'en';
  return lang.split('-')[0].toLowerCase();
}

/**
 * Get full language name from code
 */
export function getLanguageName(code) {
  return SUPPORTED_LANGUAGES[code] || code;
}

/**
 * Check if a language is supported
 */
export function isLanguageSupported(code) {
  return code in SUPPORTED_LANGUAGES;
}

export function useTranslation(userLanguage = null) {
  const [isTranslating, setIsTranslating] = useState(false);
  const translationCache = useRef(new Map()); // Cache: "text|src|tgt" -> translated

  // Get user's preferred language or detect from browser
  const targetLanguage = userLanguage || getBrowserLanguage();

  /**
   * Translate text from source language to target language
   */
  const translate = useCallback(async (text, sourceLang, targetLang = targetLanguage) => {
    // Don't translate if same language
    if (sourceLang === targetLang) {
      return { original: text, translated: text, wasTranslated: false };
    }

    // Check cache
    const cacheKey = `${text}|${sourceLang}|${targetLang}`;
    if (translationCache.current.has(cacheKey)) {
      return translationCache.current.get(cacheKey);
    }

    setIsTranslating(true);

    try {
      const response = await fetch(`${VOICE_WORKER_URL}/translate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text,
          source_lang: sourceLang,
          target_lang: targetLang,
        }),
      });

      if (!response.ok) {
        throw new Error(`Translation failed: ${response.status}`);
      }

      const data = await response.json();

      const result = {
        original: text,
        translated: data.translated,
        sourceLang,
        targetLang,
        wasTranslated: true,
      };

      // Cache the result
      translationCache.current.set(cacheKey, result);

      return result;
    } catch (error) {
      console.error('[Translation] Error:', error);
      return {
        original: text,
        translated: text,
        wasTranslated: false,
        error: error.message,
      };
    } finally {
      setIsTranslating(false);
    }
  }, [targetLanguage]);

  /**
   * Translate a message if sender's language differs from user's language
   */
  const translateMessage = useCallback(async (message, senderLanguage) => {
    if (!senderLanguage || senderLanguage === targetLanguage) {
      return null; // No translation needed
    }

    return translate(message, senderLanguage, targetLanguage);
  }, [translate, targetLanguage]);

  /**
   * Batch translate multiple messages
   */
  const translateBatch = useCallback(async (messages) => {
    const results = await Promise.all(
      messages.map(async ({ text, sourceLang }) => {
        return translate(text, sourceLang);
      })
    );
    return results;
  }, [translate]);

  /**
   * Clear translation cache
   */
  const clearCache = useCallback(() => {
    translationCache.current.clear();
  }, []);

  return {
    translate,
    translateMessage,
    translateBatch,
    clearCache,
    isTranslating,
    targetLanguage,
    supportedLanguages: SUPPORTED_LANGUAGES,
    getLanguageName,
    isLanguageSupported,
  };
}

export default useTranslation;
